import React, { useState, useEffect, useCallback } from 'react';
import { projectsApi } from '../lib/api';
import { formatDate } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Briefcase, Plus, CalendarBlank, CurrencyDollar, CheckCircle, Clock, Flag, Trash, PencilSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';

const STATUS = {
  planning:  { label: 'Planning',   color: 'bg-blue-500/10 text-blue-700 border-blue-500/30' },
  active:    { label: 'Active',     color: 'bg-emerald-500/10 text-emerald-700 border-emerald-500/30' },
  on_hold:   { label: 'On Hold',    color: 'bg-amber-500/10 text-amber-700 border-amber-500/30' },
  completed: { label: 'Completed',  color: 'bg-zinc-500/10 text-zinc-700 border-zinc-500/30' },
  cancelled: { label: 'Cancelled',  color: 'bg-destructive/10 text-destructive border-destructive/30' },
};
const PRIORITY_COLOR = { low: 'text-zinc-500', medium: 'text-blue-500', high: 'text-amber-500', critical: 'text-destructive' };
const MS_STATUS = {
  pending:     { label: 'Pending',     color: 'bg-zinc-100 text-zinc-700' },
  in_progress: { label: 'In Progress', color: 'bg-blue-100 text-blue-700' },
  done:        { label: 'Done',        color: 'bg-emerald-100 text-emerald-700' },
  blocked:     { label: 'Blocked',     color: 'bg-red-100 text-red-700' },
};

export default function ProjectsPage() {
  const [projects, setProjects] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [isFormOpen, setFormOpen] = useState(false);
  const [isMsOpen, setMsOpen] = useState(false);
  const [form, setForm] = useState({ name: '', code: '', description: '', status: 'planning', priority: 'medium', budget: '', start_date: '', end_date: '' });
  const [msForm, setMsForm] = useState({ name: '', description: '', status: 'pending', due_date: '' });

  const fetchData = useCallback(async () => {
    try {
      const [p, s] = await Promise.all([projectsApi.getAll({ limit: 100 }), projectsApi.stats()]);
      setProjects(p.data);
      setStats(s.data);
    } catch {
      toast.error('Failed to load projects');
    } finally { setLoading(false); }
  }, []);

  const fetchSelected = async (id) => {
    try {
      const res = await projectsApi.get(id);
      setSelected(res.data);
    } catch { toast.error('Failed to load project'); }
  };

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, budget: parseFloat(form.budget) || 0 };
      await projectsApi.create(payload);
      toast.success('Project created');
      setFormOpen(false);
      setForm({ name: '', code: '', description: '', status: 'planning', priority: 'medium', budget: '', start_date: '', end_date: '' });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this project?')) return;
    try {
      await projectsApi.delete(id);
      toast.success('Project deleted');
      if (selected?.id === id) setSelected(null);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleMilestoneCreate = async (e) => {
    e.preventDefault();
    if (!selected) return;
    try {
      await projectsApi.createMilestone(selected.id, msForm);
      toast.success('Milestone added');
      setMsOpen(false);
      setMsForm({ name: '', description: '', status: 'pending', due_date: '' });
      fetchSelected(selected.id);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const updateMilestoneStatus = async (ms, status) => {
    try {
      await projectsApi.updateMilestone(selected.id, ms.id, { status });
      fetchSelected(selected.id);
      fetchData();
    } catch { toast.error('Failed to update'); }
  };

  const deleteMilestone = async (ms) => {
    if (!window.confirm(`Delete milestone "${ms.name}"?`)) return;
    try {
      await projectsApi.deleteMilestone(selected.id, ms.id);
      fetchSelected(selected.id);
      fetchData();
    } catch { toast.error('Failed to delete'); }
  };

  return (
    <div className="space-y-6" data-testid="projects-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Projects</h1>
          <p className="text-muted-foreground text-sm mt-1">Plan, track and deliver initiatives</p>
        </div>
        <Button onClick={() => setFormOpen(true)} data-testid="new-project-btn"><Plus size={18} className="mr-2" /> New Project</Button>
      </div>

      {/* KPI cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <KpiCard label="Total Projects" value={stats.total_projects} icon={Briefcase} />
          <KpiCard label="Active" value={stats.by_status?.active || 0} icon={CheckCircle} color="text-emerald-500" />
          <KpiCard label="Budget Allocated" value={`$${(stats.total_budget || 0).toLocaleString()}`} icon={CurrencyDollar} color="text-blue-500" />
          <KpiCard label="Budget Spent" value={`$${(stats.total_spent || 0).toLocaleString()}`} icon={CurrencyDollar} color="text-amber-500" />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Projects list */}
        <div className="lg:col-span-1 space-y-3">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Projects</p>
          {loading ? <p className="text-muted-foreground text-sm">Loading…</p> :
            projects.length === 0 ? (
              <div className="text-center py-8 border border-dashed border-border rounded-xl">
                <Briefcase size={36} className="mx-auto text-muted-foreground opacity-40 mb-2" />
                <p className="text-sm text-muted-foreground">No projects yet</p>
              </div>
            ) : projects.map(p => (
              <button
                key={p.id}
                onClick={() => fetchSelected(p.id)}
                className={`w-full text-left bg-card border rounded-xl p-4 transition-all hover:shadow-md ${selected?.id === p.id ? 'border-primary ring-2 ring-primary/30' : 'border-border hover:border-primary'}`}
                data-testid={`project-${p.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-sm truncate">{p.name}</p>
                    <p className="text-xs text-muted-foreground">{p.code}</p>
                  </div>
                  <Flag size={14} className={PRIORITY_COLOR[p.priority] || 'text-zinc-400'} weight="fill" />
                </div>
                <div className="flex items-center justify-between mt-3">
                  <Badge variant="outline" className={`text-xs ${STATUS[p.status]?.color || ''}`}>{STATUS[p.status]?.label || p.status}</Badge>
                  <span className="text-xs text-muted-foreground">{p.milestones_done}/{p.milestone_count} ms</span>
                </div>
                <Progress value={p.progress || 0} className="h-1.5 mt-2" />
              </button>
            ))}
        </div>

        {/* Project detail */}
        <div className="lg:col-span-2">
          {!selected ? (
            <div className="h-full flex items-center justify-center text-center p-12 border border-dashed border-border rounded-xl">
              <div>
                <Briefcase size={48} className="mx-auto text-muted-foreground opacity-40 mb-3" />
                <p className="text-muted-foreground">Select a project to view details</p>
              </div>
            </div>
          ) : (
            <div className="bg-card border border-border rounded-xl p-6 space-y-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h2 className="font-heading text-2xl font-bold">{selected.name}</h2>
                  <p className="text-sm text-muted-foreground">{selected.code} · {selected.description || 'No description'}</p>
                </div>
                <div className="flex gap-2">
                  <Badge variant="outline" className={STATUS[selected.status]?.color || ''}>{STATUS[selected.status]?.label || selected.status}</Badge>
                  <Button variant="ghost" size="sm" className="text-destructive" onClick={() => handleDelete(selected.id)}><Trash size={16} /></Button>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                <div><p className="text-muted-foreground uppercase tracking-wider">Start</p><p className="font-medium mt-1">{selected.start_date ? formatDate(selected.start_date) : '—'}</p></div>
                <div><p className="text-muted-foreground uppercase tracking-wider">End</p><p className="font-medium mt-1">{selected.end_date ? formatDate(selected.end_date) : '—'}</p></div>
                <div><p className="text-muted-foreground uppercase tracking-wider">Budget</p><p className="font-medium mt-1">${(selected.budget || 0).toLocaleString()}</p></div>
                <div><p className="text-muted-foreground uppercase tracking-wider">Priority</p><p className={`font-medium mt-1 capitalize ${PRIORITY_COLOR[selected.priority]}`}>{selected.priority}</p></div>
              </div>

              {/* Milestones */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Milestones</p>
                  <Button size="sm" variant="outline" onClick={() => setMsOpen(true)} data-testid="new-milestone-btn"><Plus size={14} className="mr-1" /> Add</Button>
                </div>
                {selected.milestones?.length === 0 ? (
                  <p className="text-center text-sm text-muted-foreground py-6">No milestones yet</p>
                ) : (
                  <div className="space-y-2">
                    {selected.milestones?.map(ms => (
                      <div key={ms.id} className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
                        <Select value={ms.status} onValueChange={(v) => updateMilestoneStatus(ms, v)}>
                          <SelectTrigger className="w-36 h-8 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {Object.entries(MS_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{ms.name}</p>
                          {ms.due_date && <p className="text-xs text-muted-foreground flex items-center gap-1"><Clock size={10} />{formatDate(ms.due_date)}</p>}
                        </div>
                        <Button variant="ghost" size="sm" className="text-destructive" onClick={() => deleteMilestone(ms)}><Trash size={14} /></Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* New Project Dialog */}
      <Dialog open={isFormOpen} onOpenChange={setFormOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>New Project</DialogTitle></DialogHeader>
          <form onSubmit={handleCreate} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="p-name" /></div>
              <div><Label>Code</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="e.g. PRJ-001" /></div>
              <div><Label>Budget</Label><Input type="number" value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} /></div>
              <div><Label>Start date</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
              <div><Label>End date</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></div>
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Priority</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="low">Low</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="high">High</SelectItem><SelectItem value="critical">Critical</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label>Description</Label><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setFormOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="p-submit">Create</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* New Milestone Dialog */}
      <Dialog open={isMsOpen} onOpenChange={setMsOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Add Milestone</DialogTitle></DialogHeader>
          <form onSubmit={handleMilestoneCreate} className="space-y-3">
            <div><Label>Name *</Label><Input value={msForm.name} onChange={(e) => setMsForm({ ...msForm, name: e.target.value })} required data-testid="m-name" /></div>
            <div><Label>Due date</Label><Input type="date" value={msForm.due_date} onChange={(e) => setMsForm({ ...msForm, due_date: e.target.value })} /></div>
            <div><Label>Status</Label>
              <Select value={msForm.status} onValueChange={(v) => setMsForm({ ...msForm, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(MS_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Description</Label><Textarea rows={2} value={msForm.description} onChange={(e) => setMsForm({ ...msForm, description: e.target.value })} /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setMsOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="m-submit">Add</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KpiCard({ label, value, icon: Icon, color = 'text-primary' }) {
  return (
    <div className="bg-card border border-border rounded-xl p-4">
      <div className="flex items-center gap-3">
        <div className={`h-10 w-10 rounded-lg bg-muted flex items-center justify-center ${color}`}>
          <Icon size={20} weight="duotone" />
        </div>
        <div>
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">{label}</p>
          <p className="text-xl font-bold mt-1">{value}</p>
        </div>
      </div>
    </div>
  );
}
